import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { EVENTS_ROUTE, HOME_ROUTE } from "../../app/router/routes";
import { useAppSelector } from "../../app/store/store";
import { calendarService, IGetCalendarFilterOptionsResponse } from "../../entities/calendar";
import {
    eventsService,
    ILessonEventFilter,
    ILessonEvent,
    ILessonEventReview,
    TLessonEventFaultParty,
    TLessonEventReviewLevel,
    TLessonEventReviewStatus,
    TLessonEventType,
} from "../../entities/events";
import { useGlobalMessageActions } from "../../entities/globalMessage";
import { FEATURES, FEATURE_SECTIONS, hasFeature, useMyActions } from "../../entities/my";
import { AuthError } from "../../shared/lib/error/AuthError";
import { MyError } from "../../shared/lib/error/MyError";
import { createUtcDateFromMoscowInput, formatMoscowDateInput, formatMoscowDateTime, formatMoscowTime } from "../../shared/lib/helpers/moscowDateTime";
import { AccessDeniedRedirect } from "../../shared/ui/accessDeniedRedirect";
import { MyButton, MyButtonSecondary } from "../../shared/ui/button";
import { MyInput } from "../../shared/ui/input";
import { Modal } from "../../shared/ui/modal";
import { MyTextarea } from "../../shared/ui/myTextarea";
import { DropDownListSelected } from "../../shared/ui/dropDownSelected";
import { IItem } from "../../shared/model/types";
import { LayoutPages } from "../layoutPages";
import classes from './events.module.scss'

type TEventFilterIds = {
    tgAccountId: string;
    tutorUserId: string;
    studentId: string;
    tagId: string;
}

type TReviewDraft = {
    event: ILessonEvent;
}

type TEventsTab = 'pending' | 'history'

const EMPTY_FILTER_IDS: TEventFilterIds = {
    tgAccountId: '',
    tutorUserId: '',
    studentId: '',
    tagId: '',
}

const EVENT_TYPE_OPTIONS: {value: TLessonEventType; label: string}[] = [
    {value: 'LESSON_EVENT_TYPE_CANCELLED', label: 'Отмены'},
    {value: 'LESSON_EVENT_TYPE_RESCHEDULED', label: 'Переносы'},
    {value: 'LESSON_EVENT_TYPE_DELETED', label: 'Удаления'},
]

const FAULT_PARTY_OPTIONS: {value: TLessonEventFaultParty; label: string}[] = [
    {value: 'LESSON_EVENT_FAULT_PARTY_STUDENT', label: 'Ученик'},
    {value: 'LESSON_EVENT_FAULT_PARTY_TUTOR', label: 'Исполнитель'},
    {value: 'LESSON_EVENT_FAULT_PARTY_STAFF', label: 'Команда'},
    {value: 'LESSON_EVENT_FAULT_PARTY_UNKNOWN', label: 'Неясно'},
]

const REVIEW_LEVEL_LABELS: Record<TLessonEventReviewLevel, string> = {
    LESSON_EVENT_REVIEW_LEVEL_UNSPECIFIED: '',
    LESSON_EVENT_REVIEW_LEVEL_COORDINATOR: 'Координатор',
    LESSON_EVENT_REVIEW_LEVEL_HEAD_TEACHER: 'Главрук',
    LESSON_EVENT_REVIEW_LEVEL_ADMIN: 'Админ',
}

const REVIEW_STATUS_LABELS: Record<TLessonEventReviewStatus, string> = {
    LESSON_EVENT_REVIEW_STATUS_UNSPECIFIED: '',
    LESSON_EVENT_REVIEW_STATUS_APPROVED: 'Хорошо',
    LESSON_EVENT_REVIEW_STATUS_REJECTED: 'Плохо',
}

const EVENT_TYPE_LABELS: Record<TLessonEventType, string> = {
    LESSON_EVENT_TYPE_UNSPECIFIED: 'Событие',
    LESSON_EVENT_TYPE_CANCELLED: 'Отмена',
    LESSON_EVENT_TYPE_RESCHEDULED: 'Перенос',
    LESSON_EVENT_TYPE_DELETED: 'Удаление',
}

const FAULT_PARTY_LABELS: Record<TLessonEventFaultParty, string> = {
    LESSON_EVENT_FAULT_PARTY_STUDENT: 'Ученик',
    LESSON_EVENT_FAULT_PARTY_TUTOR: 'Исполнитель',
    LESSON_EVENT_FAULT_PARTY_STAFF: 'Команда',
    LESSON_EVENT_FAULT_PARTY_UNKNOWN: 'Неясно',
    LESSON_EVENT_FAULT_PARTY_UNSPECIFIED: 'Неясно',
}

const getReviewLevelByRole = (role: string): TLessonEventReviewLevel => {
    switch(role){
        case 'coordinator':
            return 'LESSON_EVENT_REVIEW_LEVEL_COORDINATOR'
        case 'assistant':
            return 'LESSON_EVENT_REVIEW_LEVEL_HEAD_TEACHER'
        case 'admin':
        case 'super_admin':
            return 'LESSON_EVENT_REVIEW_LEVEL_ADMIN'
        default:
            return 'LESSON_EVENT_REVIEW_LEVEL_UNSPECIFIED'
    }
}

const getStateFilterKey = (level: TLessonEventReviewLevel): 'coordinatorStates' | 'headTeacherStates' | 'adminStates' => {
    switch(level){
        case 'LESSON_EVENT_REVIEW_LEVEL_COORDINATOR':
            return 'coordinatorStates'
        case 'LESSON_EVENT_REVIEW_LEVEL_HEAD_TEACHER':
            return 'headTeacherStates'
        default:
            return 'adminStates'
    }
}

const getSlot = (event: ILessonEvent): any => {
    const payload = event.payload ?? {}
    if(event.eventType === 'LESSON_EVENT_TYPE_RESCHEDULED') return (payload as any).from
    return (payload as any).lesson
}

const slotText = (slot?: any): string => {
    if(!slot?.starts_at && !slot?.startsAt) return ''

    const startsAt = slot.starts_at ?? slot.startsAt
    const durationSlots = Number(slot.duration_slots ?? slot.durationSlots ?? 0)
    const endsAt = durationSlots
        ? new Date(new Date(startsAt).getTime() + durationSlots * 30 * 60 * 1000)
        : null

    return endsAt
        ? `${formatMoscowDateTime(startsAt, {day: '2-digit', month: '2-digit', year: 'numeric'})}, ${formatMoscowTime(startsAt)}-${formatMoscowTime(endsAt)}`
        : formatMoscowDateTime(startsAt, {day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'})
}

const getLessonLink = (event: ILessonEvent): string => {
    const slot = getSlot(event)
    const startsAt = slot?.starts_at ?? slot?.startsAt ?? event.createdAt
    const date = startsAt ? formatMoscowDateInput(startsAt) : formatMoscowDateInput(new Date())

    if(event.trialLessonId){
        return `/calendar/trial/day/${date}/lesson/${event.trialLessonId}`
    }

    return `/calendar/day/${date}/lesson/${event.lessonId}`
}

const getReviewByLevel = (event: ILessonEvent, level: TLessonEventReviewLevel): ILessonEventReview | undefined => {
    return event.reviews.find(review => review.reviewLevel === level)
}

const getVisibleReviewLevels = (role: string): TLessonEventReviewLevel[] => {
    switch(role){
        case 'coordinator':
            return ['LESSON_EVENT_REVIEW_LEVEL_COORDINATOR']
        case 'assistant':
            return [
                'LESSON_EVENT_REVIEW_LEVEL_COORDINATOR',
                'LESSON_EVENT_REVIEW_LEVEL_HEAD_TEACHER',
            ]
        case 'admin':
        case 'super_admin':
            return [
                'LESSON_EVENT_REVIEW_LEVEL_COORDINATOR',
                'LESSON_EVENT_REVIEW_LEVEL_HEAD_TEACHER',
                'LESSON_EVENT_REVIEW_LEVEL_ADMIN',
            ]
        default:
            return []
    }
}

const shouldUseAssignedFilter = (tab: TEventsTab, level: TLessonEventReviewLevel): boolean => {
    return tab === 'pending' && level !== 'LESSON_EVENT_REVIEW_LEVEL_COORDINATOR'
}

const getLessonSearch = (event: ILessonEvent): string => {
    const searchParams = new URLSearchParams()
    if(event.tgAccount?.id) searchParams.append('tgAccountIds', String(event.tgAccount.id))
    if(event.tutor?.userId) searchParams.append('userIds', String(event.tutor.userId))
    if(event.student?.id) searchParams.append('studentIds', String(event.student.id))
    searchParams.set('includeCancelled', 'true')
    searchParams.set('includeRescheduled', 'true')
    const search = searchParams.toString()

    return search ? `?${search}` : ''
}

const toggleArrayValue = <T extends string>(values: T[], value: T): T[] => {
    return values.includes(value)
        ? values.filter(item => item !== value)
        : [...values, value]
}

export default function EventsPage() {
    const {my} = useAppSelector(s => s.myReducer)
    const {setIsAuth} = useMyActions()
    const {setGlobalMessage} = useGlobalMessageActions()
    const reviewLevel = useMemo(() => getReviewLevelByRole(my.role), [my.role])
    const visibleReviewLevels = useMemo(() => getVisibleReviewLevels(my.role), [my.role])

    const [events, setEvents] = useState<ILessonEvent[]>([])
    const [total, setTotal] = useState<string>('0')
    const [isLoading, setIsLoading] = useState<boolean>(false)
    const [isFilterLoading, setIsFilterLoading] = useState<boolean>(false)
    const [fromDate, setFromDate] = useState<string>(formatMoscowDateInput(new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)))
    const [toDate, setToDate] = useState<string>(formatMoscowDateInput(new Date()))
    const [filterIds, setFilterIds] = useState<TEventFilterIds>(EMPTY_FILTER_IDS)
    const [eventTypes, setEventTypes] = useState<TLessonEventType[]>([])
    const [faultParties, setFaultParties] = useState<TLessonEventFaultParty[]>([])
    const [activeTab, setActiveTab] = useState<TEventsTab>('pending')
    const [filterOptions, setFilterOptions] = useState<IGetCalendarFilterOptionsResponse | null>(null)
    const [reviewDraft, setReviewDraft] = useState<TReviewDraft | null>(null)
    const [reviewDecision, setReviewDecision] = useState<Exclude<TLessonEventReviewStatus, 'LESSON_EVENT_REVIEW_STATUS_UNSPECIFIED'> | null>(null)
    const [reviewComment, setReviewComment] = useState<string>('')
    const [reviewError, setReviewError] = useState<string>('')
    const [isReviewLoading, setIsReviewLoading] = useState<boolean>(false)

    const isAccess = hasFeature(my, FEATURE_SECTIONS.EVENTS)
        && hasFeature(my, FEATURES.EVENTS.LIST)

    const handleError = useCallback((e: unknown, fallback: string) => {
        if(e instanceof AuthError){
            setIsAuth(false)
            setGlobalMessage({message: e.message, type: 'error'})
        }
        else if(e instanceof MyError){
            setGlobalMessage({message: e.message, type: 'error'})
        }
        else{
            setGlobalMessage({message: fallback, type: 'error'})
        }
    }, [setGlobalMessage, setIsAuth])

    const loadFilterOptions = useCallback(async () => {
        try{
            setIsFilterLoading(true)
            const response = await calendarService.getCalendarFilterOptions({
                selected: {
                    tgAccountIds: filterIds.tgAccountId ? [filterIds.tgAccountId] : [],
                    userIds: filterIds.tutorUserId ? [filterIds.tutorUserId] : [],
                    studentIds: filterIds.studentId ? [filterIds.studentId] : [],
                    tagIds: filterIds.tagId ? [filterIds.tagId] : [],
                    includeCancelled: true,
                    includeRescheduled: true,
                },
            })
            setFilterOptions(response)
        }
        catch(e){
            console.log(e)
            setFilterOptions(current => current ?? {
                viewer: {userId: String(my.id), roles: []},
                tgAccounts: [],
                tutors: [],
                students: [],
                tags: [],
            })
        }
        finally{
            setIsFilterLoading(false)
        }
    }, [filterIds, my.id])

    const loadEvents = useCallback(async () => {
        try{
            setIsLoading(true)

            const createdFrom = createUtcDateFromMoscowInput(fromDate, '00:00')?.toISOString()
            const createdTo = createUtcDateFromMoscowInput(toDate, '23:59')?.toISOString()
            const stateFilterKey = getStateFilterKey(reviewLevel)
            const filter: ILessonEventFilter = {
                createdFrom,
                createdTo,
                tgAccountIds: filterIds.tgAccountId ? [filterIds.tgAccountId] : [],
                tutorUserIds: filterIds.tutorUserId ? [filterIds.tutorUserId] : [],
                studentIds: filterIds.studentId ? [filterIds.studentId] : [],
                tagIds: filterIds.tagId ? [filterIds.tagId] : [],
                eventTypes,
                faultParties,
                onlyAssignedToMe: shouldUseAssignedFilter(activeTab, reviewLevel),
            }

            if(activeTab === 'pending'){
                filter[stateFilterKey] = ['LESSON_EVENT_REVIEW_STATE_PENDING']
            }

            const response = await eventsService.listLessonEvents({
                filter,
                limit: 100,
                offset: 0,
            })

            setEvents(response.events)
            setTotal(response.total)
        }
        catch(e){
            handleError(e, 'Ошибка при загрузке событий')
        }
        finally{
            setIsLoading(false)
        }
    }, [activeTab, eventTypes, faultParties, filterIds, fromDate, handleError, reviewLevel, toDate])

    useEffect(() => {
        if(isAccess) loadFilterOptions()
    }, [isAccess, loadFilterOptions])

    useEffect(() => {
        if(isAccess) loadEvents()
    }, [isAccess, loadEvents])

    const onFilterSelected = (field: keyof TEventFilterIds) => (item: IItem<string>) => (selected: boolean) => {
        if (!selected) return

        const value = item.id
        setFilterIds(current => {
            if(field === 'tgAccountId'){
                return {...EMPTY_FILTER_IDS, tgAccountId: value}
            }
            if(field === 'tutorUserId'){
                return {...current, tutorUserId: value, studentId: '', tagId: ''}
            }
            if(field === 'studentId'){
                return {...current, studentId: value, tagId: ''}
            }
            return {...current, tagId: value}
        })
    }

    const resetFilters = () => {
        setFilterIds(EMPTY_FILTER_IDS)
        setEventTypes([])
        setFaultParties([])
    }

    const openReview = (event: ILessonEvent) => {
        setReviewDraft({event})
        setReviewDecision(null)
        setReviewComment('')
        setReviewError('')
    }

    const submitReview = async () => {
        if(!reviewDraft || !reviewDecision) return

        try{
            setIsReviewLoading(true)
            const response = await eventsService.reviewLessonEvent({
                eventId: reviewDraft.event.id,
                reviewLevel,
                status: reviewDecision,
                comment: reviewComment.trim(),
            })
            setGlobalMessage({message: reviewDecision === 'LESSON_EVENT_REVIEW_STATUS_APPROVED' ? 'Событие отмечено хорошим' : 'Событие отмечено плохим', type: 'ok'})
            setEvents(current => current.map(event => event.id === response.event.id ? response.event : event))
            setReviewDraft(null)
            setReviewDecision(null)
            loadEvents()
        }
        catch(e){
            handleError(e, 'Ошибка при проверке события')
        }
        finally{
            setIsReviewLoading(false)
        }
    }

    const renderReviewRows = (event: ILessonEvent) => {
        return visibleReviewLevels.map(level => {
            const review = getReviewByLevel(event, level)
            return (
                <section key={level} className={classes.reviewRow}>
                    <section className={classes.reviewHeader}>
                        <span>{REVIEW_LEVEL_LABELS[level]}</span>
                        <strong className={review?.status === 'LESSON_EVENT_REVIEW_STATUS_REJECTED' ? classes.badReview : review?.status === 'LESSON_EVENT_REVIEW_STATUS_APPROVED' ? classes.goodReview : ''}>
                            {review ? REVIEW_STATUS_LABELS[review.status] : 'Не проверено'}
                        </strong>
                    </section>
                    {review && (
                        <>
                            <small className={classes.reviewerMeta}>
                                {review.reviewedBy?.fullName || '-'}
                                {review.createdAt ? ` · ${formatMoscowDateTime(review.createdAt, {day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit'})}` : ''}
                            </small>
                            {review.comment && (
                                <p className={classes.reviewComment}>{review.comment}</p>
                            )}
                        </>
                    )}
                </section>
            )
        })
    }

    if(!isAccess){
        return <AccessDeniedRedirect to={HOME_ROUTE.path} />
    }

    const options = filterOptions ?? {
        viewer: {userId: String(my.id), roles: []},
        tgAccounts: [],
        tutors: [],
        students: [],
        tags: [],
    }
    const tgAccountItems = [
        {id: '', name: 'Все'},
        ...options.tgAccounts.map(item => ({id: item.id, name: item.title || item.username})),
    ]
    const tutorItems = [
        {id: '', name: 'Все'},
        ...options.tutors.map(item => ({id: item.userId, name: item.fullName})),
    ]
    const studentItems = [
        {id: '', name: 'Все'},
        ...options.students.map(item => ({id: item.id, name: item.fullName})),
    ]
    const tagItems = [
        {id: '', name: 'Все'},
        ...options.tags.map(item => ({id: item.id, name: item.name})),
    ]

    return (
        <LayoutPages title={EVENTS_ROUTE.name}>
            <section className={classes.page}>
                <section className={classes.headerCard}>
                    <section>
                        <h1>События</h1>
                        <p>Проверка отмен и переносов занятий. Время отображается по МСК.</p>
                    </section>
                    <strong>{total}</strong>
                </section>

                <section className={classes.tabs}>
                    <button
                        type="button"
                        className={activeTab === 'pending' ? classes.selected : ''}
                        onClick={() => setActiveTab('pending')}
                    >
                        Требуется проверка
                    </button>
                    <button
                        type="button"
                        className={activeTab === 'history' ? classes.selected : ''}
                        onClick={() => setActiveTab('history')}
                    >
                        История
                    </button>
                </section>

                <section className={classes.filters}>
                    <MyInput type="date" title="С даты" value={fromDate} setValue={setFromDate} />
                    <MyInput type="date" title="По дату" value={toDate} setValue={setToDate} />

                    <section className={classes.selectField}>
                        <span>Рабочий ТГ</span>
                        <DropDownListSelected
                            isLoading={isFilterLoading}
                            items={tgAccountItems}
                            selectedIdItems={[filterIds.tgAccountId]}
                            onSelected={onFilterSelected('tgAccountId')}
                            plainSelected
                        />
                    </section>

                    <section className={classes.selectField}>
                        <span>Исполнитель</span>
                        <DropDownListSelected
                            isLoading={isFilterLoading}
                            items={tutorItems}
                            selectedIdItems={[filterIds.tutorUserId]}
                            onSelected={onFilterSelected('tutorUserId')}
                            plainSelected
                        />
                    </section>

                    <section className={classes.selectField}>
                        <span>Ученик</span>
                        <DropDownListSelected
                            isLoading={isFilterLoading}
                            items={studentItems}
                            selectedIdItems={[filterIds.studentId]}
                            onSelected={onFilterSelected('studentId')}
                            plainSelected
                        />
                    </section>

                    <section className={classes.selectField}>
                        <span>Тег</span>
                        <DropDownListSelected
                            isLoading={isFilterLoading}
                            items={tagItems}
                            selectedIdItems={[filterIds.tagId]}
                            onSelected={onFilterSelected('tagId')}
                            plainSelected
                        />
                    </section>

                    <section className={classes.toggleGroup}>
                        <span>Тип события</span>
                        <section>
                            {EVENT_TYPE_OPTIONS.map(option => (
                                <button
                                    key={option.value}
                                    type="button"
                                    className={eventTypes.includes(option.value) ? classes.selected : ''}
                                    onClick={() => setEventTypes(current => toggleArrayValue(current, option.value))}
                                >
                                    {option.label}
                                </button>
                            ))}
                        </section>
                    </section>

                    <section className={classes.toggleGroup}>
                        <span>Причина на стороне</span>
                        <section>
                            {FAULT_PARTY_OPTIONS.map(option => (
                                <button
                                    key={option.value}
                                    type="button"
                                    className={faultParties.includes(option.value) ? classes.selected : ''}
                                    onClick={() => setFaultParties(current => toggleArrayValue(current, option.value))}
                                >
                                    {option.label}
                                </button>
                            ))}
                        </section>
                    </section>

                    <section className={classes.filterActions}>
                        <MyButtonSecondary onClick={resetFilters}>Сбросить</MyButtonSecondary>
                        <MyButton onClick={loadEvents} isLoading={isLoading}>Показать</MyButton>
                    </section>
                </section>

                <section className={classes.list}>
                    {!isLoading && events.length === 0 && (
                        <section className={classes.empty}>Событий по выбранным фильтрам нет</section>
                    )}

                    {events.map(event => {
                        const payload = event.payload ?? {}
                        const from = (payload as any).from
                        const to = (payload as any).to
                        const lesson = (payload as any).lesson
                        const comment = String((payload as any).comment ?? '')
                        const canReview = activeTab === 'pending' && event.availableActions.includes('LESSON_EVENT_ACTION_REVIEW')
                        const currentReview = getReviewByLevel(event, reviewLevel)

                        return (
                            <article key={event.id} className={classes.card}>
                                <section className={classes.cardTop}>
                                    <section className={classes.titleBlock}>
                                        <span className={classes.badge}>{EVENT_TYPE_LABELS[event.eventType]}</span>
                                        <h2>{event.student?.fullName || 'Ученик не указан'}</h2>
                                    </section>
                                    <span className={classes.createdAt}>
                                        {event.createdAt ? formatMoscowDateTime(event.createdAt, {day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'}) : ''}
                                    </span>
                                </section>

                                <section className={classes.metaGrid}>
                                    <section>
                                        <span>Исполнитель</span>
                                        <strong>{event.tutor?.fullName || '-'}</strong>
                                    </section>
                                    <section>
                                        <span>Рабочий ТГ</span>
                                        <strong>{event.tgAccount?.title || event.tgAccount?.username || '-'}</strong>
                                    </section>
                                    <section>
                                        <span>Тег</span>
                                        <strong>{event.tag?.name || '-'}</strong>
                                    </section>
                                    {event.eventType !== 'LESSON_EVENT_TYPE_DELETED' && <section>
                                        <span>Причина на стороне</span>
                                        <strong>{FAULT_PARTY_LABELS[event.faultParty]}</strong>
                                    </section>}
                                </section>

                                <section className={classes.details}>
                                    {event.eventType === 'LESSON_EVENT_TYPE_RESCHEDULED' ? (
                                        <>
                                            <span>Было: {slotText(from) || '-'}</span>
                                            <span>Стало: {slotText(to) || '-'}</span>
                                        </>
                                    ) : (
                                        <span>Занятие: {slotText(lesson) || '-'}</span>
                                    )}
                                </section>

                                {comment && <p className={classes.comment}>{comment}</p>}

                                <section className={classes.reviewSummary}>
                                    {renderReviewRows(event)}
                                </section>

                                <section className={classes.footer}>
                                    <span>Создал: {event.createdBy?.fullName || '-'}{event.createdBy?.roleName ? `, ${event.createdBy.roleName}` : ''}</span>
                                    <section className={classes.actions}>
                                        {event.eventType !== 'LESSON_EVENT_TYPE_DELETED' && <Link className={classes.lessonLink} to={`${getLessonLink(event)}${event.trialLessonId ? '' : getLessonSearch(event)}`}>
                                            Урок
                                        </Link>}
                                        {canReview && reviewLevel !== 'LESSON_EVENT_REVIEW_LEVEL_UNSPECIFIED' && !currentReview && (
                                            <MyButton onClick={() => openReview(event)}>
                                                Проверить
                                            </MyButton>
                                        )}
                                    </section>
                                </section>
                            </article>
                        )
                    })}
                </section>
            </section>

            <Modal open={Boolean(reviewDraft)} setOpen={() => setReviewDraft(null)}>
                {reviewDraft && (
                    <section className={classes.reviewModal}>
                        <section className={classes.modalHeader}>
                            <section>
                                <span>Проверка события</span>
                                <h2>{reviewDraft.event.student?.fullName || 'Ученик не указан'}</h2>
                            </section>
                            <button type="button" onClick={() => setReviewDraft(null)}>×</button>
                        </section>
                        <section className={classes.reviewChoice}>
                            <button
                                type="button"
                                className={reviewDecision === 'LESSON_EVENT_REVIEW_STATUS_APPROVED' ? classes.goodSelected : ''}
                                onClick={() => setReviewDecision('LESSON_EVENT_REVIEW_STATUS_APPROVED')}
                            >
                                Хорошо
                            </button>
                            <button
                                type="button"
                                className={reviewDecision === 'LESSON_EVENT_REVIEW_STATUS_REJECTED' ? classes.badSelected : ''}
                                onClick={() => setReviewDecision('LESSON_EVENT_REVIEW_STATUS_REJECTED')}
                            >
                                Плохо
                            </button>
                        </section>
                        <MyTextarea
                            title="Комментарий"
                            value={reviewComment}
                            setValue={setReviewComment}
                            setError={setReviewError}
                            error={reviewError}
                            placeholder="Что проверили в переписке"
                        />
                        <section className={classes.modalFooter}>
                            <MyButtonSecondary onClick={() => setReviewDraft(null)}>Отмена</MyButtonSecondary>
                            <MyButton onClick={submitReview} isLoading={isReviewLoading} disabled={!reviewDecision}>Сохранить</MyButton>
                        </section>
                    </section>
                )}
            </Modal>
        </LayoutPages>
    )
}
